import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowUpRight, FiTrendingUp, FiTarget, FiShield } = FiIcons;

const stories = [
  {
    industry: "Accounting Practice",
    location: "Parramatta, NSW",
    owner: "Mark V.",
    problem: [
      "Generic template site shared by 100s of other firms",
      "Clients only calling to negotiate on price",
      "No local search visibility for high-value tax services"
    ],
    outcome: [
      "Repositioned as 'Property Tax Specialists'",
      "Ranking #1 for specialist local keywords",
      "Enquiry quality shift: 80% higher-value clients"
    ],
    metric: "4.2x Lead Quality Uplift",
    quote: "We're finally attracting the property investors we actually want to work with."
  },
  {
    industry: "Mortgage Broking",
    location: "Toowoomba, QLD",
    owner: "Sarah J.",
    problem: [
      "Website felt like a standard bank application form",
      "High bounce rate from sophisticated investors",
      "Losing prospects to larger inner-city firms"
    ],
    outcome: [
      "Authority structure focused on 'Strategic Debt'",
      "Interactive trust signals and proof points",
      "Average loan size increase of $240k"
    ],
    metric: "62% Booking Increase",
    quote: "The authority page does the heavy lifting before I even speak to the client."
  },
  {
    industry: "Psychology Clinic",
    location: "Glen Waverley, VIC",
    owner: "Dr. Priya K.",
    problem: [
      "Clinical site felt cold and unapproachable",
      "Difficult to convey the specific therapeutic focus",
      "Low trust from private-pay patients"
    ],
    outcome: [
      "Warm, authority-driven empathy architecture",
      "Clear positioning for trauma-informed care",
      "Full waitlist within 3 months of launch"
    ],
    metric: "Zero Advertising Cost",
    quote: "Our digital first impression now matches the safety we provide in clinic."
  }
];

const SuccessStories = () => {
  return (
    <section className="max-w-6xl mx-auto">
      <div className="text-center mb-20">
        <h2 className="text-4xl md:text-5xl font-bold text-white font-display mb-6">Client Success Stories</h2>
        <p className="text-slate-400 max-w-2xl mx-auto">See how shifting from a 'website' to an 'authority page' transformed these Australian practices.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {stories.map((story, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: idx * 0.1 }}
            className="glass-card flex flex-col p-8 relative overflow-hidden group"
          >
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-indigo-600 opacity-40 group-hover:opacity-100 transition-opacity" />
            
            <div className="mb-8">
              <div className="text-blue-400 text-[10px] font-black uppercase tracking-[0.2em] mb-2">{story.industry}</div>
              <h3 className="text-xl font-bold text-white font-display">{story.location}</h3>
            </div>

            <div className="space-y-8 flex-grow">
              <div>
                <p className="text-[10px] text-slate-500 uppercase tracking-widest font-bold mb-4">The Challenge</p>
                <ul className="space-y-3">
                  {story.problem.map((p, i) => (
                    <li key={i} className="text-sm text-slate-400 flex items-start gap-3">
                      <span className="w-1 h-1 rounded-full bg-red-500/40 mt-2 shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <p className="text-[10px] text-blue-500 uppercase tracking-widest font-bold mb-4">The Authority Shift</p>
                <ul className="space-y-3">
                  {story.outcome.map((o, i) => (
                    <li key={i} className="text-sm text-slate-200 flex items-start gap-3 font-medium">
                      <span className="w-1 h-1 rounded-full bg-blue-500 mt-2 shrink-0" />
                      {o}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-white/5">
              <div className="flex items-center justify-between mb-6">
                <div className="px-4 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-black uppercase">
                  {story.metric}
                </div>
                <SafeIcon icon={FiArrowUpRight} className="text-slate-600" />
              </div>
              <p className="text-sm text-slate-400 italic">"{story.quote}"</p>
              <p className="text-[10px] text-slate-500 mt-2 font-bold">— {story.owner}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default SuccessStories;